import { Path } from './Path.js';

/** @import firebase from 'firebase/compat/app' */
/** @import { KatapultBilling } from '../../../functions/source/server_modules/katapult_billing/KatapultBillingTypes.d.ts' */

// This is a way to brute force the IDE to recognize the globalThis.firebase as the browser Firebase SDK.
const firebase = /** @type {firebase.app.App} */ (/** @type {unknown} */ (globalThis.firebase));

const PAYMENT_METHOD_TYPES = ['card', 'us_bank_account'];

/**
 * Gets payment methods for a company.
 * @param {string} companyId
 * @return {Promise<{
 *  verifiedPaymentMethods: KatapultBilling.PaymentMethodSummary[],
 *  unverifiedPaymentMethods: KatapultBilling.PaymentMethodSummary[],
 *  katapultProSubscriptionPaymentMethod: KatapultBilling.PaymentMethodSummary | null
 * }>}
 */
export async function GetCompanyPaymentMethods(companyId) {
  if (typeof companyId !== 'string') throw new Error('companyId must be a string');

  /** @type {KatapultBilling.ServerRequest} */
  const request = {
    action: 'GET_COMPANY_PAYMENT_METHODS',
    companyId
  };

  const { data } = await firebase.functions().httpsCallable('katapultBilling')(request);
  return data;
}

/**
 * Creates a payment method for a company.
 * @param {string} companyId
 * @param {KatapultBilling.CreateCompanyPaymentMethod.PaymentMethodData} paymentMethodData
 * @return {Promise<KatapultBilling.PaymentMethodSummary>}
 */
export async function CreateCompanyPaymentMethod(companyId, paymentMethodData) {
  if (typeof companyId !== 'string') throw new Error('companyId must be a string');
  if (paymentMethodData == null) throw new Error('paymentMethodData is required');

  const { type } = paymentMethodData;
  if (!type) throw new Error('Missing paymentMethodData.type');
  if (!PAYMENT_METHOD_TYPES.includes(type)) throw new Error(`Unknown payment method type: ${type}`);
  if (paymentMethodData[type] == null) throw new Error(`Missing paymentMethodData.${type}`);

  /** @type {KatapultBilling.ServerRequest} */
  const request = {
    action: 'CREATE_COMPANY_PAYMENT_METHOD',
    companyId,
    data: { create_payment_method: { payment_method_data: paymentMethodData } }
  };

  const { data } = await firebase.functions().httpsCallable('katapultBilling')(request);
  return data;
}

/**
 * Verifies a bank account for a company.
 * @param {string} companyId
 * @param {string} paymentMethodId
 * @param {[number, number]} amounts
 * @return {Promise<void>}
 */
export async function VerifyCompanyBankAccount(companyId, paymentMethodId, amounts) {
  if (typeof companyId !== 'string') throw new Error('companyId must be a string');
  if (typeof paymentMethodId !== 'string') throw new Error('paymentMethodId must be a string');
  if (!Array.isArray(amounts) || amounts.length !== 2 || amounts.some(isNaN)) throw new Error('amounts must be an array of two numbers');

  /** @type {KatapultBilling.ServerRequest} */
  const request = {
    action: 'VERIFY_COMPANY_BANK_ACCOUNT',
    companyId,
    data: { verify_bank_account: { payment_method_id: paymentMethodId, amounts } }
  };

  await firebase.functions().httpsCallable('katapultBilling')(request);
}

/**
 * Sets a payment method as the default for a company's Katapult Pro subscription.
 * @param {string} companyId
 * @param {string} paymentMethodId
 * @returns {Promise<void>}
 * @throws {Error} If the company id is not a string.
 * @throws {Error} If the payment method id is not a string.
 */
export async function SetPaymentMethodUsedForCompanyKatapultProSubscription(companyId, paymentMethodId) {
  if (typeof companyId !== 'string') throw new Error('companyId must be a string');
  if (typeof paymentMethodId !== 'string') throw new Error('paymentMethodId must be a string');

  /** @type {KatapultBilling.ServerRequest} */
  const request = {
    action: 'SET_KATAPULT_PRO_SUBSCRIPTION_PAYMENT_METHOD',
    companyId,
    data: { set_katapult_pro_subscription_payment_method: { payment_method_id: paymentMethodId } }
  };

  await firebase.functions().httpsCallable('katapultBilling')(request);
}

/**
 * Removes a payment method from a company.
 * @param {string} companyId
 * @param {string} paymentMethodId
 * @returns {Promise<void>}
 * @throws {Error} If the company id is not a string.
 * @throws {Error} If the payment method id is not a string.
 */
export async function RemoveCompanyPaymentMethod(companyId, paymentMethodId) {
  if (typeof companyId !== 'string') throw new Error('companyId must be a string');
  if (typeof paymentMethodId !== 'string') throw new Error('paymentMethodId must be a string');

  /** @type {KatapultBilling.ServerRequest} */
  const request = {
    action: 'REMOVE_COMPANY_PAYMENT_METHOD',
    companyId,
    data: { remove_company_payment_method: { payment_method_id: paymentMethodId } }
  };

  await firebase.functions().httpsCallable('katapultBilling')(request);
}

/**
 * Sanitizes and validates the billing information data provided by the user.
 * @param {KatapultBilling.CompanyBillingInfo | any} billingInfo
 * @returns {{ valid: boolean, invalidPaths: { path: string, value: any, expectedType: string, isMissing: boolean, isType: boolean, isEmpty: boolean, isRequired: boolean }[], billingInfo: KatapultBilling.CompanyBillingInfo }}
 */
export function SanitizeBillingInfo(billingInfo) {
  const sanitizedBillingInfo = {};

  const results = {
    valid: false,
    invalidPaths: new Array(),
    billingInfo: /** @type { KatapultBilling.CompanyBillingInfo } */ (sanitizedBillingInfo)
  };

  const requiredPaths = ['phone', 'email', 'address.line1', 'address.city', 'address.state', 'address.postal_code', 'address.country'];

  const optionalPaths = ['address.line2'];

  for (const path of requiredPaths) {
    const value = Path.get(billingInfo, path);
    const isMissing = value == null;
    const isType = typeof value === 'string';
    const isEmpty = value.trim().length === 0;
    const isValid = !isMissing && !isEmpty && isType;
    if (isValid) {
      Path.set(sanitizedBillingInfo, path, value);
    } else {
      results.invalidPaths.push({ path, value, expectedType: 'string', isMissing, isType, isEmpty, isRequired: true });
    }
  }

  for (const path of optionalPaths) {
    const value = Path.get(billingInfo, path);
    const isMissing = value == null;
    const isType = typeof value === 'string';
    const isEmpty = value?.trim().length === 0;
    const valid = isMissing || isEmpty || isType;
    if (valid) {
      Path.set(sanitizedBillingInfo, path, value);
    } else {
      results.invalidPaths.push({ path, value, expectedType: 'string', isMissing, isType, isEmpty, isRequired: false });
    }
  }

  results.valid = results.invalidPaths.length === 0;

  return results;
}

/**
 * Gets a company's billing information.
 * @param {string} companyId
 * @returns {Promise<KatapultBilling.CompanyBillingInfo>}
 */
export async function GetCompanyBillingInfo(companyId) {
  if (typeof companyId !== 'string') throw new Error('companyId must be a string');

  /** @type {KatapultBilling.ServerRequest} */
  const request = {
    action: 'GET_COMPANY_BILLING_INFO',
    companyId
  };

  const { data } = await firebase.functions().httpsCallable('katapultBilling')(request);

  return /** @type { KatapultBilling.CompanyBillingInfo } */ (data);
}

/**
 * Sets a company's billing information.
 * @param {string} companyId
 * @param {KatapultBilling.CompanyBillingInfo} billingInfo
 * @throws {Error} If the billing info does not match the expected schema.
 */
export async function SetCompanyBillingInfo(companyId, billingInfo) {
  if (typeof companyId !== 'string') throw new Error('companyId must be a string');

  const { valid: billingInfoValid, invalidPaths, billingInfo: sanitizedBillingInfo } = SanitizeBillingInfo(billingInfo);
  if (!billingInfoValid) {
    const [firstInvalidPath] = invalidPaths;
    if (firstInvalidPath.isMissing) throw new Error(`Missing billingInfo.${firstInvalidPath.path}`);
    if (!firstInvalidPath.isType) throw new Error(`Expected billingInfo.${firstInvalidPath.path} to be a ${firstInvalidPath.expectedType}`);
    if (firstInvalidPath.isEmpty) throw new Error(`Expected billingInfo.${firstInvalidPath.path} to not be empty`);
    throw new Error(`Invalid value at ${firstInvalidPath.path}`);
  }

  /** @type {KatapultBilling.ServerRequest} */
  const request = {
    action: 'SET_COMPANY_BILLING_INFO',
    companyId,
    data: { set_company_billing_info: { billing_info: sanitizedBillingInfo } }
  };

  await firebase.functions().httpsCallable('katapultBilling')(request);
}

/**
 * Creates a new customer billing portal session.
 * @param {string} companyId
 * @returns {Promise<KatapultBilling.CustomerPortalSessionSummary>}
 */
export async function CreateCustomerPortalSession(companyId) {
  const currentUrl = window.location.href ?? '';

  /** @type {KatapultBilling.ServerRequest} */
  const request = {
    action: 'CREATE_CUSTOMER_BILLING_PORTAL_SESSION',
    companyId,
    data: { create_customer_billing_portal_session: { return_url: currentUrl } }
  };

  const { data } = await firebase.functions().httpsCallable('katapultBilling')(request);

  return /** @type { KatapultBilling.CustomerPortalSessionSummary } */ (data);
}

/**
 * Checks if a company has manual billing enabled.
 * @param {string} companyId
 * @returns {Promise<{manualBilling: boolean}>}
 */
export async function CheckCompanyManualBilling(companyId) {
  /** @type {KatapultBilling.ServerRequest} */
  const request = {
    action: 'CHECK_COMPANY_MANUAL_BILLING',
    companyId
  };

  const { data } = await firebase.functions().httpsCallable('katapultBilling')(request);

  return data;
}
